// LocationPickerModal.jsx
// A modal that lets the user pick a store location either by:
//  1) searching an address / detecting via GPS (reuses NewPlaceAutocomplete), or
//  2) dragging a pin on an interactive map to the exact spot.
import React, { useState, useRef, useCallback } from "react";
import { X, MapPin, LocateFixed, Search, Check, Loader2 } from "lucide-react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import NewPlaceAutocomplete from "./NewPlaceAutocomplete";

const libraries = ["places"];
const mapContainerStyle = {
  width: "100%",
  height: "340px",
  borderRadius: "0.75rem",
};
// Fallback center used before the user searches, detects, or drags the pin.
const defaultCenter = { lat: 26.8467, lng: 80.9462 }; // Lucknow

const extractComponent = (components, type) =>
  components.find((c) => c.types.includes(type))?.long_name || "";

// Normalizes a Google geocoding result into the same shape NewPlaceAutocomplete
// already produces, so parent components don't need two different data shapes.
const buildLocationData = (place, coords) => {
  const components = place.address_components || [];
  return {
    latitude: coords.lat,
    longitude: coords.lng,
    formattedAddress: place.formatted_address,
    street: extractComponent(components, "route"),
    streetNumber: extractComponent(components, "street_number"),
    locality: extractComponent(components, "locality"),
    sublocality: extractComponent(components, "sublocality"),
    city:
      extractComponent(components, "locality") ||
      extractComponent(components, "administrative_area_level_2"),
    state: extractComponent(components, "administrative_area_level_1"),
    country: extractComponent(components, "country"),
    postalCode: extractComponent(components, "postal_code"),
    neighborhood: extractComponent(components, "neighborhood"),
    displayAddress: place.formatted_address,
    shortAddress: `${extractComponent(components, "locality")}, ${extractComponent(
      components,
      "administrative_area_level_1"
    )}`,
    area:
      extractComponent(components, "sublocality") ||
      extractComponent(components, "neighborhood") ||
      extractComponent(components, "locality"),
    placeId: place.place_id,
    locationType: "map_pin",
  };
};

const LocationPickerModal = ({ isOpen, onClose, onLocationSelect, apiKey }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const [activeTab, setActiveTab] = useState("search"); // "search" | "map"
  const [markerPos, setMarkerPos] = useState(defaultCenter);
  const [previewAddress, setPreviewAddress] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [mapError, setMapError] = useState("");

  const mapRef = useRef(null);
  const geocoderRef = useRef(null);

  const reverseGeocode = useCallback((coords) => {
    if (!window.google) return;
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    setIsGeocoding(true);
    setMapError("");
    geocoderRef.current.geocode({ location: coords }, (results, status) => {
      setIsGeocoding(false);
      if (status === "OK" && results?.[0]) {
        setPreviewAddress(buildLocationData(results[0], coords));
      } else {
        setMapError("Couldn't find an address for this spot. Try dragging the pin again.");
        setPreviewAddress(null);
      }
    });
  }, []);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleMarkerDragEnd = useCallback(
    (e) => {
      const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setMarkerPos(coords);
      reverseGeocode(coords);
    },
    [reverseGeocode]
  );

  const handleMapClick = useCallback(
    (e) => {
      const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      setMarkerPos(coords);
      reverseGeocode(coords);
    },
    [reverseGeocode]
  );

  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      setMapError("Geolocation isn't supported by this browser.");
      return;
    }
    setIsLocating(true);
    setMapError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setMarkerPos(coords);
        mapRef.current?.panTo(coords);
        mapRef.current?.setZoom(16);
        reverseGeocode(coords);
        setIsLocating(false);
      },
      () => {
        setMapError("Location access denied. Enable it in your browser settings.");
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleConfirmMapPin = () => {
    if (previewAddress) {
      onLocationSelect?.(previewAddress);
      onClose();
    }
  };

  const handleSearchSelect = (locationData) => {
    onLocationSelect?.(locationData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal panel */}
      <div className="relative bg-white w-full max-w-xl rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <MapPin size={18} className="text-[#f4a406]" />
            Choose store location
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 border-b">
          <button
            type="button"
            onClick={() => setActiveTab("search")}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === "search"
                ? "border-[#f4a406] text-[#f4a406]"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Search size={14} /> Search or detect
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("map")}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === "map"
                ? "border-[#f4a406] text-[#f4a406]"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <MapPin size={14} /> Pick on map
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto">
          {activeTab === "search" && (
            <NewPlaceAutocomplete onLocationSelect={handleSearchSelect} apiKey={apiKey} />
          )}

          {activeTab === "map" && (
            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-xs text-gray-500">
                  Drag the pin, or tap the map, to set the exact spot.
                </p>
                <button
                  type="button"
                  onClick={handleLocateMe}
                  disabled={isLocating || !isLoaded}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-[#f4a406] text-white hover:bg-[#e69605] disabled:bg-gray-300 whitespace-nowrap"
                >
                  {isLocating ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <LocateFixed size={12} />
                  )}
                  My location
                </button>
              </div>

              {!isLoaded ? (
                <div className="h-[340px] flex items-center justify-center bg-gray-50 rounded-xl border text-sm text-gray-500">
                  Loading map...
                </div>
              ) : (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={markerPos}
                  zoom={14}
                  onLoad={handleMapLoad}
                  onClick={handleMapClick}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                >
                  <Marker position={markerPos} draggable onDragEnd={handleMarkerDragEnd} />
                </GoogleMap>
              )}

              {mapError && <p className="mt-2 text-sm text-red-600">{mapError}</p>}

              {isGeocoding && (
                <p className="mt-2 text-sm text-gray-500 flex items-center gap-1.5">
                  <Loader2 size={14} className="animate-spin" /> Finding address for this spot...
                </p>
              )}

              {previewAddress && !isGeocoding && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-gray-800">
                    {previewAddress.displayAddress}
                  </p>
                  {previewAddress.area && (
                    <p className="text-xs text-gray-500 mt-1">Area: {previewAddress.area}</p>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={handleConfirmMapPin}
                disabled={!previewAddress || isGeocoding}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-[#f4a406] text-white py-2.5 rounded-xl hover:bg-[#d89005] disabled:bg-gray-300 transition-colors font-medium"
              >
                <Check size={16} /> Confirm this location
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;